import React, { useEffect, useState, createContext } from 'react';
import { useSession, useFeatures } from '@cordata/buffet';
import { getAccessToken } from '@cordata/buffet/build/helpers/token';
import { Socket } from 'phoenix-socket';
import PropTypes from 'prop-types';
import camelcaseKeys from 'camelcase-keys';

import { authorizedPyPublicInstance as Public } from '~/utils';

const disconnectSocket = (s) => {
  s.disconnect(() => console.log('socket closed'));

  return null;
};

const SubscriptionContext = createContext({ subscriptions: undefined });

const SubscriptionProvider = ({ children, socketEndpoint, channels }) => {
  const [socket, setSocket] = useState();
  const [subscriptions, setSubscriptions] = useState([]);
  const [topics, setTopics] = useState({});
  const {
    isAuthed, current, currentAppUser, fetchAppUser,
  } = useSession();
  const { hasAppNavigator, chat } = useFeatures();
  const [lastMessage, setLastMessage] = useState();

  useEffect(() => {
    if (isAuthed && current && hasAppNavigator && chat) {
      fetchAppUser(current);
    }
  }, [isAuthed, current, hasAppNavigator]);

  useEffect(() => {
    if (currentAppUser && process.env.REACT_APP_WS_HOST_NAME) {
      Public
        .get('real-time-subscription', {
          params: {
            recipient_user_uid: currentAppUser.id,
          },
        })
        .then(r => r.data.data)
        .then((r) => {
          setSubscriptions(r);
          setTopics(r.reduce((a, m) => ({
            ...a,
            [m.attributes.realTimeTopicUid]: {
              newMessages: 0,
            },
          }), {}));

          const socketParams = { params: { Authorization: `Bearer ${getAccessToken()}` } };

          setSocket(new Socket(new URL(`${process.env.REACT_APP_WS_HOST_NAME}/${socketEndpoint}`), socketParams));
        });
    }

    return () => {
      if (socket) {
        setSocket(disconnectSocket(socket));
      }
    };
  }, [currentAppUser, socketEndpoint]);

  useEffect(() => {
    if (socket && socket.connectionState() !== 'connecting') {
      socket.connect();
    }
  }, [socket]);

  useEffect(() => {
    if (topics && socket) {
      channels.forEach((c) => {
        const channel = socket.channel(c.name);

        channel.join();

        c.on.forEach((chan) => {
          channel.on(chan, (m) => {
            const msg = camelcaseKeys(m, { deep: true });

            setLastMessage(msg);
          });
        });
      });
    }
  }, [socket]);

  useEffect(() => {
    if (lastMessage) {
      const topicUid = lastMessage.topic.realTimeTopicUid;

      setTopics({
        ...topics,
        [topicUid]: {
          topic: lastMessage.topic,
          lastMessage,
          newMessages: topics[topicUid] ? topics[topicUid].newMessages + 1 : 1,
        },
      });
    }
  }, [lastMessage]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptions,
        topics,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

SubscriptionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  socketEndpoint: PropTypes.string,
  channels: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

SubscriptionProvider.defaultProps = {
  socketEndpoint: 'socket',
};

export { SubscriptionContext, SubscriptionProvider };

